import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Activity, Users, Shield, Search, ChevronLeft, ChevronRight, BarChart3, Clock, User, Filter, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

const ACTION_COLORS = {
  create: 'bg-emerald-100 text-emerald-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  login: 'bg-violet-100 text-violet-800',
  submit: 'bg-amber-100 text-amber-800',
  export: 'bg-cyan-100 text-cyan-800',
  assign: 'bg-indigo-100 text-indigo-800',
};

const ENTITY_COLORS = {
  test: 'bg-blue-50 text-blue-700 border-blue-200',
  user: 'bg-violet-50 text-violet-700 border-violet-200',
  question_bank: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  question: 'bg-teal-50 text-teal-700 border-teal-200',
  attempt: 'bg-amber-50 text-amber-700 border-amber-200',
  batch: 'bg-pink-50 text-pink-700 border-pink-200',
  proctoring: 'bg-red-50 text-red-700 border-red-200',
};

function getActionColor(action) {
  const key = Object.keys(ACTION_COLORS).find(k => action?.toLowerCase().includes(k));
  return key ? ACTION_COLORS[key] : 'bg-gray-100 text-gray-800';
}

function getEntityColor(entity) {
  const key = Object.keys(ENTITY_COLORS).find(k => entity?.toLowerCase().includes(k));
  return key ? ENTITY_COLORS[key] : 'bg-gray-50 text-gray-700 border-gray-200';
}

function formatTimeAgo(timestamp) {
  const now = new Date();
  const then = new Date(timestamp);
  const diff = Math.floor((now - then) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
  return then.toLocaleDateString();
}

const PAGE_SIZE = 50;

function AuditLogs() {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);

  // Filters
  const [entityFilter, setEntityFilter] = useState('all');
  const [actionFilter, setActionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    fetchStats();
  }, []);

  useEffect(() => {
    fetchLogs();
  }, [page, entityFilter, actionFilter]);

  const fetchStats = async () => {
    try {
      const response = await api.get('/audit-logs/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching audit stats:', error);
    }
  };

  const fetchLogs = async () => {
    setLoading(true);
    try {
      const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE };
      if (entityFilter !== 'all') params.entity = entityFilter;
      if (actionFilter !== 'all') params.action = actionFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await api.get('/audit-logs', { params });
      setLogs(response.data.logs);
      setTotal(response.data.total);
    } catch (error) {
      console.error('Error fetching logs:', error);
      toast.error('Failed to load activity logs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPage(0);
    fetchLogs();
  };

  const handleRefresh = () => {
    fetchStats();
    fetchLogs();
    toast.success('Refreshed');
  };

  const entityOptions = useMemo(() => {
    if (!stats?.by_entity) return [];
    return Object.entries(stats.by_entity).sort((a, b) => b[1] - a[1]);
  }, [stats]);

  const actionOptions = useMemo(() => {
    if (!stats?.by_action) return [];
    return Object.entries(stats.by_action).sort((a, b) => b[1] - a[1]);
  }, [stats]);

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900" data-testid="audit-logs-title">
              Admin Activity Monitor
            </h1>
            <p className="text-gray-500 mt-1">Track and review all administrative actions across the portal</p>
          </div>
          <Button variant="outline" onClick={handleRefresh} data-testid="refresh-logs-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Activities</p>
                  <p className="text-2xl font-bold text-gray-900" data-testid="total-activities">{stats.total.toLocaleString()}</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-xl">
                  <Activity className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Entity Types</p>
                  <p className="text-2xl font-bold text-gray-900">{Object.keys(stats.by_entity).length}</p>
                </div>
                <div className="p-3 bg-emerald-50 rounded-xl">
                  <BarChart3 className="w-5 h-5 text-emerald-600" />
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Action Types</p>
                  <p className="text-2xl font-bold text-gray-900">{Object.keys(stats.by_action).length}</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-xl">
                  <Shield className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </Card>
            <Card className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Active Admins</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.top_users.filter(u => u.role !== 'user').length}</p>
                </div>
                <div className="p-3 bg-violet-50 rounded-xl">
                  <Users className="w-5 h-5 text-violet-600" />
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* Top Users + Entity Breakdown */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card className="p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <User className="w-4 h-4" /> Most Active Users
              </h3>
              <div className="space-y-2">
                {stats.top_users.slice(0, 6).map((u, i) => (
                  <div key={i} className="flex items-center justify-between py-1.5">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-500 w-5">{i + 1}.</span>
                      <span className="text-sm font-medium text-gray-900">{u.name}</span>
                      <Badge variant="outline" className="text-xs">{u.role}</Badge>
                    </div>
                    <span className="text-sm font-semibold text-gray-700">{u.count}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card className="p-5">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                <BarChart3 className="w-4 h-4" /> Activity by Entity
              </h3>
              <div className="space-y-2">
                {entityOptions.slice(0, 6).map(([entity, count], i) => {
                  const maxCount = entityOptions[0]?.[1] || 1;
                  const pct = Math.round((count / maxCount) * 100);
                  return (
                    <div key={entity}>
                      <div className="flex items-center justify-between text-sm mb-1">
                        <span className="font-medium text-gray-700 capitalize">{entity}</span>
                        <span className="text-gray-500">{count}</span>
                      </div>
                      <div className="w-full bg-gray-100 rounded-full h-2">
                        <div className="bg-blue-500 h-2 rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            <Filter className="w-4 h-4 text-gray-400" />
            <Select value={entityFilter} onValueChange={(v) => { setEntityFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="entity-filter">
                <SelectValue placeholder="All Entities" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Entities</SelectItem>
                {entityOptions.map(([entity]) => (
                  <SelectItem key={entity} value={entity}>{entity}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={actionFilter} onValueChange={(v) => { setActionFilter(v); setPage(0); }}>
              <SelectTrigger className="w-[180px]" data-testid="action-filter">
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {actionOptions.map(([action]) => (
                  <SelectItem key={action} value={action}>{action}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex items-center gap-2 flex-1 min-w-[200px]">
              <Input
                placeholder="Search activities..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                className="max-w-xs"
                data-testid="search-input"
              />
              <Button variant="outline" size="icon" onClick={handleSearch}>
                <Search className="w-4 h-4" />
              </Button>
            </div>
            <div className="text-sm text-gray-500 ml-auto">
              {total.toLocaleString()} results
            </div>
          </div>
        </Card>

        {/* Activity Table */}
        <Card className="overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400">
              <Activity className="w-10 h-10 mb-2" />
              <p>No activities found</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="audit-logs-table">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Time</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Action</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Entity</th>
                      <th className="px-5 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Details</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-100">
                    {logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50 transition-colors" data-testid={`log-row-${log.id}`}>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-1.5 text-sm text-gray-500">
                            <Clock className="w-3.5 h-3.5" />
                            <span title={new Date(log.timestamp).toLocaleString()}>
                              {formatTimeAgo(log.timestamp)}
                            </span>
                          </div>
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-600">
                              {(log.user_name || 'S')[0].toUpperCase()}
                            </div>
                            <div>
                              <p className="text-sm font-medium text-gray-900">{log.user_name || 'System'}</p>
                              <p className="text-xs text-gray-400">{log.user_role || ''}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getActionColor(log.action)}`}>
                            {log.action}
                          </span>
                        </td>
                        <td className="px-5 py-3 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-medium border ${getEntityColor(log.entity)}`}>
                            {log.entity}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-sm text-gray-500 max-w-xs truncate">
                          {log.meta && Object.keys(log.meta).length > 0 ? (
                            <span title={JSON.stringify(log.meta, null, 2)}>
                              {log.meta.name || log.meta.title || log.meta.test_name || log.meta.email || JSON.stringify(log.meta).slice(0, 60)}
                            </span>
                          ) : (
                            <span className="text-gray-300">-</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-5 py-3 border-t bg-gray-50">
                <p className="text-sm text-gray-500">
                  Showing {page * PAGE_SIZE + 1}-{Math.min((page + 1) * PAGE_SIZE, total)} of {total.toLocaleString()}
                </p>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page === 0}
                    onClick={() => setPage(p => p - 1)}
                    data-testid="prev-page-btn"
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <span className="text-sm text-gray-600">
                    Page {page + 1} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages - 1}
                    onClick={() => setPage(p => p + 1)}
                    data-testid="next-page-btn"
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </Card>
      </div>
    </Layout>
  );
}

export default AuditLogs;
